import React from 'react';
import { useForm } from 'react-hook-form';
import { Button, VStack, HStack, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers';

import { useSignup, SignUpProps } from '@olo-web/domain/customer';
import { HookFormInput } from '@olo-web/components/molecules/HookFormInput/HookFormInput.ui';
import { Banner } from '@olo-web/components/atoms/Banner';
import { validationSchema } from './utils/validationSchema';
import { useMerchantDisplayName } from '@domain/merchants/hooks/useMerchantDisplayName';

export function SignUpForm({ onSuccess }: { onSuccess?: (data: any) => void }): JSX.Element {
  const { register, handleSubmit, errors, control } = useForm({
    resolver: yupResolver(validationSchema),
  });
  const { isLoading, error, mutate, reset } = useSignup({ onSuccess });
  const LegalPhoneText = () => {
    const merchantName = useMerchantDisplayName();
    return (
      <Text textStyle="label" color="blackAlpha.350">
        By providing your phone number, you consent to receive text messages from{' '}
        {merchantName || 'this merchant'} related to your order.
      </Text>
    );
  };

  const onSubmit = async (data: SignUpProps) => {
    await mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-testid="sign-up-modal">
      <VStack spacing={6}>
        <HStack spacing={6} w="100%">
          <HookFormInput
            isRequired
            isUsingYup
            label="First name"
            name="firstName"
            nameReq="first name required"
            type="text"
            placeholder="Ian"
            autoComplete="given-name"
            errors={errors}
            ref={register}
            data-testid="first-name-input"
          />
          <HookFormInput
            isRequired
            isUsingYup
            label="Last name"
            name="lastName"
            nameReq="last name required"
            type="text"
            placeholder="Hungry"
            autoComplete="family-name"
            errors={errors}
            ref={register}
            data-testid="last-name-input"
          />
        </HStack>
        <HookFormInput
          isRequired
          isUsingYup
          label="Email"
          type="email"
          autoComplete="email"
          errors={errors}
          name="email"
          nameReq="email required"
          placeholder="yummy@food.com"
          ref={register}
          data-testid="email-input"
        />
        <HookFormInput
          isRequired
          isUsingYup
          label="Phone"
          type="tel"
          autoComplete="tel"
          errors={errors}
          name="phone"
          nameReq="phone number required"
          placeholder="(555) 555-5555"
          control={control}
          data-testid="phone-input"
        />
        <LegalPhoneText />
        <HookFormInput
          isRequired
          isUsingYup
          label="Password"
          type="password"
          autoComplete="new-password"
          errors={errors}
          name="password"
          nameReq="password required"
          placeholder="•••••••••"
          ref={register}
          data-testid="signup-password-input"
        />
        <HookFormInput
          isRequired
          isUsingYup
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          errors={errors}
          name="confirmPassword"
          nameReq="password required"
          placeholder="•••••••••"
          ref={register}
          data-testid="confirm-password-input"
        />
      </VStack>
      <VStack mt={6} mb={4} spacing={4}>
        {error && (
          <Banner
            status="error"
            error={error}
            title="Account creation unsuccessful"
            onClose={reset}
            mb={2}
          />
        )}
        <Button
          size="lg"
          w="100%"
          colorScheme="primary"
          _focus={{
            boxShadow: 'none',
          }}
          _focusVisible={{
            boxShadow: 'none',
            outline: '3px solid #000',
            outlineOffset: '3px',
          }}
          loadingText="Creating your account..."
          isLoading={isLoading}
          type="submit"
          data-testid="create-account-button"
        >
          Create account
        </Button>
      </VStack>
    </form>
  );
}
